import type { FlowSlotDto, FlowDto } from '@/types/dtos/flows/response/flow.dto';
import { People, PeopleOutline, VideoLibrary } from '@mui/icons-material';
import { Stack, type SvgIconProps } from '@mui/material';
import type { MediaDto } from '@/types/dtos/media/media.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { EventDto } from '@/types/dtos/events/response/event.dto';
import {
  getFlowContributors,
  getContributorFlowRoles,
  getContributorSessionRolesForInvite,
  getRoleInviteUnitForFlowSlotContext,
  resolveOrganizeFlowSlotIdForInvite,
  resolveRoleInviteUnitsIndicatorColor,
  getSessionContributors,
  type RoleContextInviteUnit,
} from '@/utils/contributor-extraction.utils';
import {
  collectRemoteSpeakerAggregateColorForContext,
  worstContributorStatusColor,
} from '@/utils/remote-speaker-status.utils';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { resolveMediaListIndicatorColor } from '@/helpers/media-item-aggregate-status.helper';

type EventScheduleRef = Pick<EventDto, 'slots'> | { slots?: Array<{ id: string; day?: string | { id: string } }> };

const iconSize = { height: 20, width: 20 };

/** Slot types without contributor roles in the list row status column. */
function slotTypeShowsContributorStatus(contentType?: FlowSlotDto['contentType']): boolean {
  return contentType !== 'Break';
}

/** One unit per flow/session role row; colors aligned with moderator start row via {@link resolveRoleInviteUnitsIndicatorColor}. */
function getContributorColor(
  slot?: FlowSlotDto,
  flow?: FlowDto,
  allContributors?: ContributorDto[],
  sessionContent?: FlowSlotDto['content'],
  flowOrganize?: FlowDto,
  event?: EventScheduleRef,
  eventSlotId?: string,
): SvgIconProps['color'] {
  if (!allContributors) {
    return 'disabled';
  }

  const inviteFlow = flowOrganize ?? flow;
  const inviteFlowSlotId = resolveOrganizeFlowSlotIdForInvite(slot, flowOrganize);
  const units: RoleContextInviteUnit[] = [];

  if (inviteFlow) {
    const flowContributors = getFlowContributors(inviteFlow, allContributors);
    const flowId = typeof inviteFlow.origin === 'string' ? inviteFlow.origin : inviteFlow.id;

    flowContributors.forEach((contributor) => {
      const flowRoles = getContributorFlowRoles(contributor, flowId);
      flowRoles.forEach((role) => {
        units.push(getRoleInviteUnitForFlowSlotContext(role, inviteFlowSlotId));
      });
    });
  }

  const contentToCheck = sessionContent ?? slot?.content;
  let sessionId: string | undefined;
  if (
    slot?.contentType === 'Session' &&
    contentToCheck &&
    typeof contentToCheck === 'object' &&
    'id' in contentToCheck
  ) {
    sessionId = contentToCheck.id;
    const sessionContributors = getSessionContributors(contentToCheck as SessionDto, allContributors);

    sessionContributors.forEach((contributor) => {
      const sessionRoles = getContributorSessionRolesForInvite(contributor, contentToCheck as SessionDto);
      sessionRoles.forEach((role) => {
        units.push(getRoleInviteUnitForFlowSlotContext(role, inviteFlowSlotId));
      });
    });
  }

  const inviteColor = resolveRoleInviteUnitsIndicatorColor(units);
  const flowId = inviteFlow ? (typeof inviteFlow.origin === 'string' ? inviteFlow.origin : inviteFlow.id) : undefined;
  const remoteColor = collectRemoteSpeakerAggregateColorForContext({
    allContributors,
    event,
    eventSlotId,
    flowSlotId: inviteFlowSlotId,
    flowId,
    sessionId,
  });

  return worstContributorStatusColor(inviteColor, remoteColor);
}

function getMediaColor(
  slot?: FlowSlotDto,
  flow?: FlowDto,
  sessionContent?: FlowSlotDto['content'],
): SvgIconProps['color'] {
  // Collect all relevant media items
  const relevantMedia: MediaDto[] = [];

  // For all slot types (Session, Break, etc.), we check:
  // 1. Flow media with placement matching this slot ID (flow slot media)
  if (flow && Array.isArray(flow.media) && slot?.id) {
    const slotId = slot.id;
    const flowSlotMedia = flow.media.filter((media) => {
      // Only include flow media with placement matching this slot
      // Ensure placement exists, is of type 'Slot', and ID matches
      return media.placement?.type === 'Slot' && media.placement.id === slotId;
    });
    relevantMedia.push(...flowSlotMedia);
  }

  // 2. Session media: use reactive sessionContent if available, otherwise fall back to slot.content
  // This ensures we get the latest session media updates reactively
  const contentToCheck = sessionContent ?? slot?.content;
  if (contentToCheck && typeof contentToCheck === 'object' && 'media' in contentToCheck) {
    const contentMedia = contentToCheck.media;
    if (Array.isArray(contentMedia)) {
      relevantMedia.push(...contentMedia);
    }
  }

  return resolveMediaListIndicatorColor(relevantMedia);
}

export default function ItemIcons({
  slot,
  flow,
  flowOrganize,
  sessionContent,
  allContributors,
  event,
  eventSlotId,
}: {
  slot?: FlowSlotDto;
  flow?: FlowDto;
  flowOrganize?: FlowDto;
  sessionContent?: FlowSlotDto['content'];
  allContributors?: ContributorDto[];
  event?: EventScheduleRef;
  eventSlotId?: string;
}) {
  const showContributorStatus = slotTypeShowsContributorStatus(slot?.contentType);
  const contributorColor = showContributorStatus
    ? getContributorColor(slot, flow, allContributors, sessionContent, flowOrganize, event, eventSlotId)
    : undefined;
  const mediaColor = getMediaColor(slot, flow, sessionContent);

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      gap={1}
      sx={{ p: 1, width: '100%', minWidth: 0, height: '100%', flexShrink: 0, boxSizing: 'border-box' }}
    >
      {showContributorStatus ? (
        contributorColor === 'primary' ? (
          <People color="primary" sx={iconSize} />
        ) : (
          <PeopleOutline color={contributorColor} sx={iconSize} />
        )
      ) : null}
      <VideoLibrary color={mediaColor} sx={iconSize} />
    </Stack>
  );
}
